/**
 * Notification Inbox Component
 *
 * Full-page notification inbox with:
 * - List all notifications
 * - Filter: All / Unread
 * - Mark as read on click
 * - Mark all as read button
 * - Navigate to related entity on click
 * - Show relative time
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  FileText,
  UserPlus,
  MessageSquare,
  AlertCircle,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';

export function NotificationInbox() {
  const navigate = useNavigate();
  const { role } = useSupabaseAuth();
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refresh
  } = useNotifications();

  const [filter, setFilter] = useState('all'); // 'all' or 'unread'

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read_at)
    : notifications;

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read_at) {
      markAsRead(notification.id);
    }

    // Navigate to related entity
    const { notification_type, related_rfq_id, related_application_id, related_request_id } = notification;

    if (notification_type === 'rfq_inclusion_requested' || notification_type === 'rfq_invited') {
      if (role === 'recruiter' || role === 'admin') {
        navigate(`/recruiter/rfqs`);
      }
    } else if (notification_type === 'new_application') {
      if (role === 'recruiter' || role === 'admin') {
        navigate(`/recruiter/applications`);
      }
    } else if (notification_type === 'candidate_request') {
      if (role === 'recruiter' || role === 'admin') {
        navigate(`/recruiter/requests`);
      }
    } else if (notification_type === 'file_deletion_request') {
      if ((role === 'recruiter' || role === 'admin') && notification.related_message_id) {
        navigate(`/recruiter/candidates/${notification.related_message_id}`);
      }
    } else if (
      ['application_status_changed', 'application_unsuccessful', 'client_feedback_added', 'feedback_request'].includes(notification_type)
    ) {
      if (role === 'candidate') {
        navigate('/candidate/applications');
      }
    } else if (notification_type === 'application_update_request') {
      if (role === 'recruiter' || role === 'admin') {
        navigate('/recruiter/requests');
      }
    }
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return notifTime.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getIcon = (type) => {
    const iconClass = "w-5 h-5";

    switch (type) {
      case 'rfq_inclusion_requested':
      case 'rfq_invited':
        return <FileText className={iconClass} />;
      case 'new_application':
        return <UserPlus className={iconClass} />;
      case 'candidate_request':
        return <AlertCircle className={iconClass} />;
      case 'new_message':
        return <MessageSquare className={iconClass} />;
      default:
        return <Bell className={iconClass} />;
    }
  };

  const getNotificationStyle = (notification) => {
    if (notification.read_at) {
      return 'bg-slate-50 border-slate-200 text-slate-600';
    }

    switch (notification.notification_type) {
      case 'rfq_invited':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'new_application':
        return 'bg-blue-50 border-blue-200 text-blue-900';
      case 'candidate_request':
        return 'bg-amber-50 border-amber-200 text-amber-900';
      default:
        return 'bg-white border-slate-300 text-slate-900';
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-slate-600" />
              <div>
                <CardTitle>Notifications</CardTitle>
                {unreadCount > 0 && (
                  <p className="text-sm text-slate-600 mt-1">
                    {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Refresh button */}
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>

              {/* Mark all as read */}
              {unreadCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={markAllAsRead}
                >
                  <CheckCheck className="w-4 h-4 mr-2" />
                  Mark All Read
                </Button>
              )}
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-2 mt-4">
            <Filter className="w-4 h-4 text-slate-500" />
            <button
              onClick={() => setFilter('all')}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${filter === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-600 hover:bg-slate-100'
                }
              `}
            >
              All ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${filter === 'unread'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-600 hover:bg-slate-100'
                }
              `}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </CardHeader>

        <CardContent>
          {loading && (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600">Loading notifications...</p>
            </div>
          )}

          {!loading && filteredNotifications.length === 0 && (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </h3>
              <p className="text-slate-600">
                {filter === 'unread'
                  ? "You're all caught up!"
                  : "Notifications will appear here when you receive them"
                }
              </p>
            </div>
          )}

          {!loading && filteredNotifications.length > 0 && (
            <div className="space-y-2">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    ${getNotificationStyle(notification)}
                    border rounded-lg p-4 cursor-pointer
                    transition-all hover:shadow-md
                  `}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getIcon(notification.notification_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-semibold text-sm">
                          {notification.title}
                        </h4>
                        <span className="text-xs opacity-70 flex-shrink-0">
                          {getRelativeTime(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-sm opacity-90">
                        {notification.body}
                      </p>
                    </div>

                    {/* Read indicator */}
                    {!notification.read_at && (
                      <div className="flex-shrink-0">
                        <Badge variant="default" className="bg-blue-500">
                          New
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
